import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ChartLineUp } from '@phosphor-icons/react';
import api from '@/lib/api';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#F97316'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-xl">
      <p className="text-xs text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color || p.fill }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

export default function TrafficAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/analytics/traffic'), api.get('/analytics/revenue')])
      .then(([t, r]) => setData({ traffic: t.data, revenue: r.data }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-zinc-400">Failed to load analytics.</p>;

  const { traffic, revenue } = data;

  return (
    <div className="space-y-6" data-testid="traffic-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <ChartLineUp size={32} className="text-blue-400" weight="duotone" />
            Traffic Intelligence
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Global traffic flow analysis and revenue metrics</p>
        </div>
        <div className="flex gap-4">
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">MRR</p>
            <p className="text-xl font-bold font-heading text-emerald-400">${(revenue?.mrr || 0).toLocaleString()}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">ARPU</p>
            <p className="text-xl font-bold font-heading text-blue-400">${revenue?.arpu || 0}</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white">Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={revenue?.daily || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#27272A' }}
                  tickFormatter={v => v?.split('-').slice(1).join('/')} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#27272A' }}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="revenue" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.08} strokeWidth={2} dot={false} name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white">Traffic by Source</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={traffic?.by_source || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#27272A' }}
                  tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="source" tick={{ fill: '#A1A1AA', fontSize: 11 }} tickLine={false} axisLine={false} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="clicks" fill="#3B82F6" radius={[0, 4, 4, 0]} name="Clicks" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white">Clicks Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={traffic?.over_time || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#27272A' }}
                  tickFormatter={v => v?.split('-').slice(1).join('/')} />
                <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#27272A' }}
                  tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="clicks" stroke="#10B981" fill="#10B981" fillOpacity={0.08} strokeWidth={2} dot={false} name="Clicks" />
                <Area type="monotone" dataKey="conversions" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.05} strokeWidth={2} dot={false} name="Conversions" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white">Geographic Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <ResponsiveContainer width="50%" height={260}>
                <PieChart>
                  <Pie data={traffic?.by_geo || []} dataKey="clicks" nameKey="geo" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {(traffic?.by_geo || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2 py-4">
                {(traffic?.by_geo || []).slice(0, 6).map((g, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-sm text-zinc-300">{g.geo}</span>
                    </div>
                    <span className="text-xs text-zinc-400 font-mono">{(g.clicks || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
